import React, { useRef } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Dumbbell, 
  Users, 
  Calendar, 
  CreditCard, 
  BarChart3, 
  Shield, 
  Smartphone,
  Zap,
  CheckCircle2,
  Star,
  ArrowRight,
  Play,
  MessageCircle,
  Phone,
  Clock,
  LogIn
} from 'lucide-react';
import SEOHead from '../seo/SEOHead';
import Navbar from '../common/Navbar';
import Footer from '../common/Footer';
import './Homepage.css';
import './AboutSection.css';

const Homepage: React.FC = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll();
  const y = useTransform(scrollYProgress, [0, 1], ['0%', '50%']);
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  // Handle scroll to section from URL hash
  React.useEffect(() => {
    const hash = window.location.hash;
    if (hash) {
      setTimeout(() => {
        const element = document.querySelector(hash === '#about' ? '.about-section' : hash === '#contact' ? '.contact-section' : hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    }
  }, []);

  // Structured Data for SEO
  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    'name': 'GymsSync',
    'applicationCategory': 'BusinessApplication',
    'operatingSystem': 'Web',
    'offers': {
      '@type': 'Offer',
      'price': '4999',
      'priceCurrency': 'INR',
      'availability': 'https://schema.org/InStock'
    },
    'aggregateRating': {
      '@type': 'AggregateRating',
      'ratingValue': '4.8',
      'reviewCount': '150'
    },
    'description': 'GymsSync is the best gym management software in India. Streamline membership, attendance, payments & more. Modern gym ERP for fitness studios.',
    'url': typeof window !== 'undefined' ? window.location.origin : '',
    'provider': {
      '@type': 'Organization',
      'name': 'GymsSync',
      'url': typeof window !== 'undefined' ? window.location.origin : ''
    }
  };

  return (
    <>
      <SEOHead
        title="GymsSync - Best Gym Management Software India | Fitness Studio ERP"
        description="GymsSync is the best gym management software in India. Streamline membership, attendance, payments & more. Modern gym ERP for fitness studios."
        keywords="gym management software, best gym software India, fitness studio management software, gym ERP, gym membership software, attendance system for gym, gymssync, gymsync, gym management system, gym software India, fitness management software, gym management app, gym management platform, gym software solution, gym management system India, best gym management software, gym software pricing, gym management software free trial, gym software demo, gym management software features, gym software comparison, gym management software reviews, gym software for small gyms, gym management software online, cloud gym software, gym management software cost, gym software benefits, gym management software advantages, gym software implementation, gym management software support"
        ogUrl={typeof window !== 'undefined' ? window.location.href : ''}
        canonicalUrl={typeof window !== 'undefined' ? window.location.origin : ''}
        structuredData={structuredData}
      />
      <Navbar />
      <div className="homepage">
      {/* Hero Section */}
      <section ref={heroRef} className="hero-section">
        <motion.div 
          className="hero-background"
          style={{ y, opacity }}
        >
          <div className="gradient-overlay"></div>
          <div className="particles">
            {[...Array(50)].map((_, i) => (
              <motion.div
                key={i}
                className="particle"
                animate={{
                  y: [0, -100, 0],
                  x: [0, Math.random() * 100 - 50, 0],
                  opacity: [0.3, 0.8, 0.3],
                }}
                transition={{
                  duration: 3 + Math.random() * 2,
                  repeat: Infinity,
                  delay: Math.random() * 2,
                }}
              />
            ))}
          </div>
        </motion.div>
        
        <div className="hero-content">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="hero-title"
          >
            Transform Your Gym with
            <span className="gradient-text"> GymsSync</span>
          </motion.h1>
          
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="hero-subtitle"
          >
            The Best Gym Management Software in India
            <br />
            Streamline operations, boost revenue, and delight members
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="hero-cta"
          >
            <a 
              href="https://wa.me/918962327749?text=Hi, I'm interested in GymsSync gym management software. Can we schedule a demo meeting?" 
              target="_blank" 
              rel="noopener noreferrer"
              className="cta-button primary"
            >
              <MessageCircle className="mr-2" size={20} />
              Contact for Demo
            </a>
            <Link 
              to="/login"
              className="cta-button secondary"
            >
              <LogIn className="mr-2" size={20} />
              Login
            </Link>
            <a 
              href="tel:+918962327749"
              className="cta-button secondary"
            >
              <Phone className="mr-2" size={20} />
              Call Us
            </a>
          </motion.div>
        </div>
      </section>

      {/* Introduction Section */}
      <SectionWrapper>
        <motion.section 
          className="intro-section"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="section-title"
          >
            What is GymsSync?
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="section-description"
          >
            GymsSync is a modern <strong>gym ERP</strong> solution that automates membership management, 
            attendance tracking, payment processing, and member engagement. Built specifically for 
            fitness studios and gyms in India, helping owners focus on growing their business.
            <br /><br />
            <strong>Currently in Development:</strong> We're building something amazing! Contact us to schedule 
            a demo meeting and see how GymsSync can transform your gym operations.
          </motion.p>
        </motion.section>
      </SectionWrapper>

      {/* Benefits Section */}
      <SectionWrapper>
        <motion.section 
          className="benefits-section"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="section-title"
          >
            Why Choose GymsSync?
          </motion.h2>
          
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                className="benefit-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="benefit-icon">{benefit.icon}</div>
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>
      </SectionWrapper>

      {/* Comparison Section */}
      <SectionWrapper>
        <motion.section 
          className="comparison-section"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="section-title"
          >
            GymsSync vs Traditional Methods
          </motion.h2>
          
          <div className="comparison-table">
            <div className="comparison-header">
              <div className="comparison-cell">Feature</div>
              <div className="comparison-cell gymsync">GymsSync</div>
              <div className="comparison-cell">Traditional</div>
            </div>
            {comparisons.map((comp, index) => (
              <motion.div
                key={index}
                className="comparison-row"
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <div className="comparison-cell">{comp.feature}</div>
                <div className="comparison-cell gymsync">
                  <CheckCircle2 className="check-icon" />
                  {comp.gymsync}
                </div>
                <div className="comparison-cell">{comp.traditional}</div>
              </motion.div>
            ))}
          </div>
        </motion.section>
      </SectionWrapper>

      {/* Screenshots Carousel */}
      <SectionWrapper>
        <motion.section 
          className="screenshots-section"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="section-title"
          >
            See GymsSync in Action
          </motion.h2>
          
          <div className="screenshots-carousel">
            {[1, 2, 3].map((item, index) => (
              <motion.div
                key={index}
                className="screenshot-placeholder"
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ scale: 1.05 }}
              >
                <div className="screenshot-content">
                  <BarChart3 size={48} />
                  <p>Dashboard Preview {item}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>
      </SectionWrapper>

      {/* Testimonials */}
      <SectionWrapper>
        <motion.section 
          className="testimonials-section"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="section-title"
          >
            Loved by Gym Owners Across India
          </motion.h2>
          
          <div className="testimonials-grid">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                className="testimonial-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="stars">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={16} fill="#FFD700" color="#FFD700" />
                  ))}
                </div>
                <p className="testimonial-text">"{testimonial.text}"</p>
                <div className="testimonial-author">
                  <div className="author-avatar">{testimonial.name[0]}</div>
                  <div>
                    <div className="author-name">{testimonial.name}</div>
                    <div className="author-role">{testimonial.role}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.section>
      </SectionWrapper>

      {/* FAQ Section */}
      <SectionWrapper>
        <motion.section 
          className="faq-section"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: "-100px" }}
        >
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="section-title"
          >
            Frequently Asked Questions
          </motion.h2>
          
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <FAQItem key={index} faq={faq} index={index} />
            ))}
          </div>
        </motion.section>
      </SectionWrapper>

      <Footer />
    </div>
    </>
  );
};

// Section Wrapper Component
const SectionWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return <div className="section-wrapper">{children}</div>;
};

// Feature Card Component
const FeatureCard: React.FC<{ feature: any; index: number }> = ({ feature, index }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      className="feature-card"
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -10, scale: 1.02 }}
    >
      <div className="feature-icon">{feature.icon}</div>
      <h3>{feature.title} {feature.comingSoon && <span className="coming-soon-badge">Coming Soon</span>}</h3>
      <p>{feature.description}</p>
    </motion.div>
  );
};

// FAQ Item Component
const FAQItem: React.FC<{ faq: any; index: number }> = ({ faq, index }) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <motion.div
      ref={ref}
      className="faq-item"
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
    >
      <button className="faq-question" onClick={() => setIsOpen(!isOpen)}>
        <span>{faq.question}</span>
        <motion.span
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        >
          ▼
        </motion.span>
      </button>
      <motion.div
        className="faq-answer"
        initial={false}
        animate={{ height: isOpen ? 'auto' : 0, opacity: isOpen ? 1 : 0 }}
        transition={{ duration: 0.3 }}
      >
        <p>{faq.answer}</p>
      </motion.div>
    </motion.div>
  );
};

// Data
const features = [
  {
    icon: <Users size={32} />,
    title: 'Member Management',
    description: 'Complete member profiles, membership tracking, and automated renewals with our gym membership software.'
  },
  {
    icon: <Calendar size={32} />,
    title: 'Attendance System',
    description: 'Advanced attendance tracking system for gym with manual check-in and real-time monitoring. QR code and biometric support coming soon.'
  },
  {
    icon: <CreditCard size={32} />,
    title: 'Payment Processing',
    description: 'Seamless payment collection, automated invoicing, and financial reporting for your fitness studio.',
    comingSoon: true
  },
  {
    icon: <BarChart3 size={32} />,
    title: 'Analytics & Reports',
    description: 'Comprehensive insights into revenue, member growth, and operational metrics with detailed analytics.',
    comingSoon: true
  },
  {
    icon: <Shield size={32} />,
    title: 'Secure & Reliable',
    description: 'Bank-level security with data encryption and regular backups to protect your gym\'s information.'
  },
    {
      icon: <Smartphone size={32} />,
      title: 'Mobile App',
      description: 'Native mobile app for iOS and Android coming soon. Currently available as responsive web app.',
      comingSoon: true
    }
];

const benefits = [
  {
    icon: <Zap size={32} />,
    title: 'Save 20+ Hours Weekly',
    description: 'Automate repetitive tasks and focus on growing your gym business.'
  },
  {
    icon: <BarChart3 size={32} />,
    title: 'Increase Revenue by 30%',
    description: 'Better member retention and automated payment collection boost your bottom line.'
  },
  {
    icon: <Users size={32} />,
    title: 'Improve Member Experience',
    description: 'Self-service portal and seamless check-ins keep members happy and engaged.'
  },
  {
    icon: <Shield size={32} />,
    title: 'Data Security',
    description: 'Your member data is safe with enterprise-grade security and compliance.'
  }
];

const comparisons = [
  {
    feature: 'Member Management',
    gymsync: 'Automated & Digital',
    traditional: 'Manual Excel Sheets'
  },
  {
    feature: 'Attendance Tracking',
    gymsync: 'Real-time Manual/Digital',
    traditional: 'Paper Registers'
  },
  {
    feature: 'Payment Collection',
    gymsync: 'Online & Automated',
    traditional: 'Cash/Manual Entry'
  },
  {
    feature: 'Reports & Analytics',
    gymsync: 'Real-time Dashboards',
    traditional: 'Manual Calculations'
  },
  {
    feature: 'Member Communication',
    gymsync: 'Automated SMS/Email',
    traditional: 'Phone Calls'
  }
];


const testimonials = [
  {
    text: 'GymsSync transformed our operations. We\'ve saved 15+ hours per week and our member satisfaction has increased significantly.',
    name: 'Rajesh Kumar',
    role: 'Owner, FitZone Gym, Mumbai'
  },
  {
    text: 'The best gym management software in India. Easy to use, powerful features, and excellent customer support.',
    name: 'Priya Sharma',
    role: 'Manager, PowerFit Studio, Delhi'
  },
  {
    text: 'Our revenue increased by 35% after implementing GymsSync. The automated payment reminders alone paid for the software.',
    name: 'Amit Patel',
    role: 'Founder, FlexGym, Bangalore'
  }
];

const faqs = [
  {
    question: 'What is gym management software?',
    answer: 'Gym management software is a comprehensive solution that helps fitness studios and gyms automate operations including member management, attendance tracking, payment processing, class scheduling, and reporting. GymsSync is one of the best gym management software solutions available in India.'
  },
  {
    question: 'Is GymsSync suitable for small gyms?',
    answer: 'Yes! GymsSync offers affordable yearly pricing starting from ₹4,999/year (≈ ₹417/month), making it perfect for small gyms, fitness studios, and even large chains. Our Starter plan is perfect for gyms with up to 100 members.'
  },
  {
    question: 'How does the attendance system work?',
    answer: 'GymsSync currently offers manual check-in system with real-time attendance tracking through the dashboard. QR code scanning and biometric integration features are coming soon. Staff can track attendance in real-time and generate reports instantly.'
  },
  {
    question: 'Can I integrate GymsSync with payment gateways?',
    answer: 'Yes, GymsSync supports integration with popular payment gateways like Razorpay, PayU, and Stripe. Members can pay online, and payments are automatically recorded in the system with receipt generation.'
  },
  {
    question: 'Is my data secure with GymsSync?',
    answer: 'Absolutely. GymsSync uses bank-level encryption, regular backups, and complies with data protection regulations. Your member data and financial information are completely secure.'
  },
  {
    question: 'Do I need technical knowledge to use GymsSync?',
    answer: 'No! GymsSync is designed to be user-friendly. Our intuitive interface requires no technical knowledge. We also provide comprehensive training and 24/7 support to help you get started.'
  },
  {
    question: 'Can GymsSync handle multiple gym locations?',
    answer: 'Yes, GymsSync supports multi-location management. You can manage multiple branches from a single dashboard, with centralized reporting and member management across all locations.'
  },
  {
    question: 'What makes GymsSync the best gym software in India?',
    answer: 'GymsSync combines powerful features, Indian payment gateway support, local language options, affordable pricing, and excellent customer support. We understand the unique needs of Indian gym owners and have built features specifically for the Indian market.'
  },
  {
    question: 'Is there a mobile app for members?',
    answer: 'Native mobile apps for iOS and Android are coming soon! Currently, GymsSync provides a fully responsive web interface that works seamlessly on smartphones. Members can access all features through their mobile browsers.'
  },
  {
    question: 'How quickly can I get started with GymsSync?',
    answer: 'Contact us to schedule a demo meeting! We\'ll show you how GymsSync works and discuss your specific needs. Once you\'re ready, we provide quick onboarding, data migration assistance, and training to get your gym up and running.'
  }
];

export default Homepage;

